import Link from 'next/link'
import { useRouter } from 'next/router'
import { enqueueSnackbar } from 'notistack'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { logOut } from '@omnea-app/fe-app-shell/headers/logOut'
import { OrgMenu } from '@omnea-app/fe-app-shell/headers/OrgMenu/OrgMenu'
import { OrgToggle } from '@omnea-app/fe-app-shell/headers/OrgToggle/OrgToggle'
import { useOrgRoleLabel } from '@omnea-app/fe-app-shell/headers/useOrgRoleLabel'
import { useAvailableOrganisations } from '@omnea-app/fe-auth/useAvailableOrganisations'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { useSwitchOrganisation } from '@omnea-app/fe-auth/useSwitchOrganisation'
import { LoggedInUserAvatar } from '@omnea-app/fe-users-and-groups/avatar/UserAvatar'
import { settingsHrefFrom } from './settingsHref'

/** The top bar's organisation and account pill, and the menu it opens: settings, log out and switching organisation. */
export const ShellAccount = () => {
  const { t } = useTranslation('login')
  const { user, org } = useAuthenticatedUser()
  const { data, isError } = useAvailableOrganisations()
  const switchOrganisation = useSwitchOrganisation()
  const roleLabel = useOrgRoleLabel()
  const { asPath } = useRouter()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const menuId = useId()

  const availableOrgs = data?.availableOrganisations ?? []
  const currentOrg = availableOrgs.find(({ id }) => id === org.organisationId)
  const userName = `${user.firstName} ${user.lastName}`

  return (
    <>
      <OrgToggle
        orgName={org.name}
        userName={userName}
        logoSrc={currentOrg?.logoUrl ?? undefined}
        darkLogoSrc={currentOrg?.logoUrlDark ?? undefined}
        qa={org.isQaOrg}
        avatar={<LoggedInUserAvatar user={user} size={24} />}
        onClick={(event) => setAnchor(event.currentTarget)}
        aria-haspopup="dialog"
        aria-expanded={anchor != null}
        aria-controls={anchor != null ? menuId : undefined}
      />
      <OrgMenu
        id={menuId}
        open={anchor != null}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        userName={userName}
        userEmail={user.email}
        avatar={<LoggedInUserAvatar user={user} size={32} />}
        // Still loading, the menu shows placeholder rows; a failed load leaves the list out rather than loading forever.
        orgs={
          data?.availableOrganisations.map((availableOrg) => ({
            id: availableOrg.id,
            name: availableOrg.name,
            role: roleLabel(availableOrg),
            iconSrc: availableOrg.iconUrl ?? undefined,
            qa: availableOrg.isQa,
          })) ?? (isError ? [] : undefined)
        }
        currentOrgId={org.organisationId}
        onSwitchOrg={(orgId) =>
          switchOrganisation(orgId).catch(() => enqueueSnackbar(t('choose-organisation.switch-error'), { variant: 'error' }))
        }
        settingsHref={settingsHrefFrom(asPath)}
        LinkComponent={Link}
        onLogOut={logOut}
      />
    </>
  )
}
